import type { ManagedProject, ScmProviderDescriptor } from '../api/types';

/**
 * Natureza do `project_key` na tela de Gestão: projeto (Azure DevOps, que
 * tem camada de projeto dentro da organização) ou owner/organização (GitHub
 * e outros providers flat, onde o próprio owner é a chave RBAC).
 */
export type ScopeKind = ManagedProject['scope'];

/** Rótulos PT-BR da entidade selecionada na Gestão, com gênero correto. */
export interface ScopeLabels {
  /** "Projeto" | "Organização" */
  singular: string;
  /** "Projetos" | "Organizações" */
  plural: string;
  /** "projeto" | "organização" */
  lower: string;
  /** "um projeto" | "uma organização" */
  indefinite: string;
  /** "neste projeto" | "nesta organização" */
  inThis: string;
  /** "do Projeto" | "da Organização" */
  ofThe: string;
  /** "do projeto" | "da organização" */
  ofTheLower: string;
  /** "ao Projeto" | "à Organização" */
  toThe: string;
}

export const PROJECT_SCOPE_LABELS: ScopeLabels = {
  singular: 'Projeto',
  plural: 'Projetos',
  lower: 'projeto',
  indefinite: 'um projeto',
  inThis: 'neste projeto',
  ofThe: 'do Projeto',
  ofTheLower: 'do projeto',
  toThe: 'ao Projeto',
};

export const OWNER_SCOPE_LABELS: ScopeLabels = {
  singular: 'Organização',
  plural: 'Organizações',
  lower: 'organização',
  indefinite: 'uma organização',
  inThis: 'nesta organização',
  ofThe: 'da Organização',
  ofTheLower: 'da organização',
  toThe: 'à Organização',
};

export function scopeLabelsFor(kind: ScopeKind): ScopeLabels {
  return kind === 'owner' ? OWNER_SCOPE_LABELS : PROJECT_SCOPE_LABELS;
}

/**
 * Decide o termo a exibir. Com um item selecionado, vale o escopo dele. Sem
 * seleção, só troca para "organização" quando TODOS os itens disponíveis são
 * owners (deploy GitHub puro) — num deploy misto, "projeto" segue como termo
 * genérico.
 */
export function resolveScopeKind(
  projects: readonly ManagedProject[],
  selected?: ManagedProject | null,
): ScopeKind {
  if (selected) return selected.scope;
  if (projects.length > 0 && projects.every(p => p.scope === 'owner')) {
    return 'owner';
  }
  return 'project';
}

export function resolveScopeLabels(
  projects: readonly ManagedProject[],
  selected?: ManagedProject | null,
): ScopeLabels {
  return scopeLabelsFor(resolveScopeKind(projects, selected));
}

/**
 * Mesmo critério de `resolveScopeKind` sem item selecionado, mas a partir dos
 * providers configurados: só vira "organização" quando TODOS são flat
 * (GitHub puro). Sem providers conhecidos, "projeto" segue como termo padrão.
 */
export function resolveScopeKindFromProviders(
  providers: readonly ScmProviderDescriptor[],
): ScopeKind {
  if (providers.length > 0 && providers.every(p => !p.hierarchy.hasProject)) {
    return 'owner';
  }
  return 'project';
}
